import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore.js';
import SetupNotice from '../components/SetupNotice.js';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export default function Register() {
  const navigate = useNavigate();
  const loginStore = useAuthStore((state) => state.login);

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerLoading, setRegisterLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    if (!email || !username || !password || !confirmPassword) return setRegisterError('Please fill in all fields');
    if (password.length < 6) return setRegisterError('Password must be at least 6 characters');
    if (password !== confirmPassword) return setRegisterError('Passwords do not match');

    setRegisterLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, { username, email, password });
      const { access_token, refresh_token, user } = response.data;
      await loginStore(access_token, refresh_token, user);
      navigate('/home');
    } catch (err: any) {
      setRegisterError(err.response?.data?.message || 'Registration failed');
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-margin-mobile select-none">
      <div className="w-full max-w-md bg-[#141414] thin-border rounded-lg p-gutter">
        <div className="text-center mb-6">
          <h1 className="font-headline-xl text-headline-lg text-on-surface mb-2">Create Account</h1>
          <p className="text-on-surface-variant text-body-sm">Join the PVP Academy network.</p>
        </div>

        <SetupNotice />

        <form onSubmit={handleRegister} className="space-y-4">
          {registerError && (
            <div className="p-3 mb-2 bg-error/10 border border-error/20 text-error rounded">
              {registerError}
            </div>
          )}

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Email Address
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="name@domain.com"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface outline-none"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Username
            </label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              type="text"
              placeholder="e.g. PvP_Master"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface outline-none"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Password
            </label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              placeholder="Min 6 characters"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface outline-none"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Confirm Password
            </label>
            <input
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type="password"
              placeholder="Re-enter password"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={registerLoading}
            className="w-full bg-error text-white py-3 rounded font-bold"
          >
            {registerLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-on-surface-variant text-[13px] mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-error hover:underline font-medium ml-1">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
}